import Vue from 'vue';
import '@/plugins/vue-cookie';
import {authResource} from '@/modules/Auth/resources/auth.resources';
import {IAuth} from '@/modules/Auth/interfaces/auth.interface';
import {AxiosResponse} from 'axios';
import {IResponseData} from '@/interfaces/response.interface';
import {AppError} from '@/plugins/axios';
import {IRequestAuth} from '@/modules/Auth/interfaces/request-auth.interface';
import router from '@/router';
import {ActionContext} from 'vuex';
import {IApp} from "@/interfaces/app.interface";
import ROUTE_NAME = IApp.ROUTE_NAME;
import {IAppState} from "@/interfaces/app-state.interface";
import {httpResource} from "@/resources/http.resource";
import {IRequestProfileSet} from "@/modules/Profile/interfaces/request-profile-set.interface";
import {bus} from "@/plugins/bus";
import {ICorp} from "@/modules/Corps/interfaces/corp.interface";
import {OpenPageParams} from "@/store/store";

const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_IS_USER_KEY = 'auth_is_user';
const AUTH_LOGIN_KEY = 'auth_login';
const AUTH_PROFILE_NAME = 'auth_profile_name';
const AUTH_ANONYM_TOKEN = 'auth_anonym_token';
const AUTH_IS_SHOW_ERROR_SESSION_MESSAGE = 'auth_is_show_error_session_message';
const AUTH_IS_ACTUAL_ERROR_SESSION_MESSAGE = 'auth_is_actual_error_session_message';
const URL_FOR_REDIRECT_AFTER_LOGIN = 'url_for_redirect_after_login';

const CONFIRM_EMAIL = 'confirm_email';
const CONFIRM_REGISTRATION = 'reg';
const RECOVER_PASSWORD = 'confirm_password';
export const ANONYMOUS_PAGE = 'anonymous_page';
const ANONYMOUS_TOKEN = '7FB4E46F-E923-4757-A780-00D2C7CBF4B1';

type AuthStore = ActionContext<IAuth.IState, IAppState>;

export const AuthStore: AuthStore = {
  //@ts-ignore
  namespaced: true,
  state: {
    token: '',
    profileName: '',
    login: '',
    password: '',
    recoverPassword: '',
    isUser: false,
    isNewSession: false,
    isShowDialogSession: false,
    tokenWasChecked: false,
    userAccess: {},
  },

  getters: {},

  mutations: {
    setProperty(state: IAuth.IState, { name, value }: { name: string, value: any}) { Vue.set(state, name, value); },
    updateUserAccessProp(state: IAuth.IState, data: { name: string; value: any; }) { Vue.set(state.userAccess, data.name, data.value); },
    setToken(state: IAuth.IState, token: string) { state.token = token; },
    setNewSession(state: IAuth.IState) { state.isNewSession = true; },
    setPassword(state: IAuth.IState, password: string) { state.password = password; },
    setRecoverPassword(state: IAuth.IState, password: string) { state.recoverPassword = password; },

    setLogin(state: IAuth.IState, login: string) {
      state.login = login;
      Vue.cookie.set(AUTH_LOGIN_KEY, login, {expires: '10Y'});
    },

    setIsUser(state: IAuth.IState, isUser: boolean) {
      state.isUser = isUser;
      Vue.cookie.set(AUTH_IS_USER_KEY, JSON.stringify(isUser), {expires: '10Y'});
    },

    setProfileName(state: IAuth.IState, profileName: string) {
      state.profileName = profileName;
      Vue.cookie.set(AUTH_PROFILE_NAME, profileName, {expires: '10Y'});
    },
  },

  actions: {
    async init({state, commit, dispatch, rootState}: AuthStore) {
      const pagesWithoutLogin = [
        IApp.ROUTE_NAME.PAGE_SUPPORT,
        IApp.ROUTE_NAME.PAGE_SCREEN_PAY,
        IApp.ROUTE_NAME.PAGE_AUTHORIZATION,
        IApp.ROUTE_NAME.PAGE_REGISTRATION,
      ];
      const loggedNow = Vue.cookie.get(AUTH_IS_USER_KEY) === 'true';

      const anonymousPage = rootState.route.query[ANONYMOUS_PAGE];
      if (anonymousPage) {
        await dispatch('_setToken', ANONYMOUS_TOKEN);
        commit('setIsUser', true);
        return;
      }

      /**
       * ------------------------
       * Если залогиненный пользователь перешел по реферальной ссылке,
       * разлогиневаем его и запускаем по обычной обработке рефералки.
       */
      const refCode = rootState.route.query[ICorp.UrlGetParams.RefLink];
      if (refCode && refCode !== '' && loggedNow && rootState.route.name === IApp.ROUTE_NAME.PAGE_AUTHORIZATION) {
        await dispatch('_setToken', Vue.cookie.get(AUTH_TOKEN_KEY));
        return await dispatch('logout', { withoutRedirect: true });
      }
      /** ------------------------ */

      /**
       * ------------------------
       * Обработка параметроф реферальной ссылки.
       */
      const nedRegByRef = localStorage.getItem(ICorp.StorageKeys.NeedRegByRef);
      if (nedRegByRef && JSON.parse(nedRegByRef) && rootState.route.name !== IApp.ROUTE_NAME.PAGE_PROFILE) {
        if (pagesWithoutLogin.includes(rootState.route.name)) {
          localStorage.setItem(ICorp.StorageKeys.NeedRegByRef, JSON.stringify(false));
          localStorage.setItem(ICorp.StorageKeys.URLRegByRef, '');
          await dispatch('_setToken', '');
          return;
        }
        else {
          const url = JSON.parse(localStorage.getItem(ICorp.StorageKeys.URLRegByRef));
          const payload = {routeName: IApp.ROUTE_NAME.REDIRECT, url};
          return dispatch('openPage', payload, {root: true});
        }
      }
      /** ------------------------ */

      const confirmEmail = rootState.route.query[CONFIRM_EMAIL];
      if (confirmEmail && rootState.route.name !== ROUTE_NAME.PAGE_CONFIRM_EMAIL) {
        return dispatch('openPage', {routeName: ROUTE_NAME.PAGE_CONFIRM_EMAIL}, {root: true});
      }

      const confirmRegistration = rootState.route.query[CONFIRM_REGISTRATION];
      const recoverPasswordGuid = rootState.route.query[RECOVER_PASSWORD];
      if (confirmRegistration || recoverPasswordGuid) {
        return dispatch('checkToken')
          .then(() => dispatch('registrationConfirm'));
      }

      await dispatch('loadIsShowErrorSessionMessage');
      if (Vue.cookie.get(IAuth.CookieName.UserSometimeLogin) === 'true' && !loggedNow && !pagesWithoutLogin.includes(rootState.route.name)) {
        return dispatch('_clearCookie')
          .then(() => dispatch('saveUrlForRedirectAfterLogin'))
          .then(() => dispatch('openPage', {routeName: ROUTE_NAME.PAGE_AUTHORIZATION}, {root: true}));
      }
      else {
        await dispatch('_loadUser');
        const token = Vue.cookie.get(AUTH_TOKEN_KEY);
        return token
          ? dispatch('_setToken', token)
            .then(() => { if (!state.tokenWasChecked) dispatch('checkToken'); })
            .then(() => commit('setProperty', { name: 'tokenWasChecked', value: true }))
          : dispatch('_auth').then(() => dispatch('checkToken'));
      }
    },

    clear({dispatch}: AuthStore) {
      return dispatch('_setToken', '');
    },

    clearAnonymToken({dispatch}: AuthStore) {
      return dispatch('_setAnonymToken', '');
    },

    resolveError({dispatch, state}: AuthStore, error: AppError) {
      if (error.status === IRequestAuth.ErrorCode.TokenExpired && state.isUser) {
        // token already exist in store
        return dispatch('_auth');
      } else {
        // clear and get new token by _auth
        return dispatch('clear')
          .then(() => {
            if (error.status === IRequestAuth.ErrorCode.TokenExpired || error.status === IRequestAuth.ErrorCode.TokenInvalid) {
              return dispatch('clearAnonymToken');
            }
          })
          .then(() => dispatch('_auth'));
      }
    },

    async login({commit, dispatch, state}: AuthStore, payload?: { loginId: number; }) {
      commit('setRecoverPassword', '');
      let params: any = {
        loginName: state.login,
        password: state.password,
        command: IRequestAuth.Command.Login,
      };
      if (payload && payload.loginId) params.loginId = payload.loginId;

      let anonymToken = Vue.cookie.get(AUTH_ANONYM_TOKEN);
      if (anonymToken) params.anonymToken = anonymToken;

      try {
        let response = await httpResource.httpAction({ action: IRequestAuth.Action.Auth, params });

        if (response.data.result.userCorps) {
          return response.data.result;
        }

        let authData: IRequestAuth.IAuthDataResponse = IRequestAuth.prepareAuthData(response.data.result);
        await dispatch('_setToken', authData.token);

        if (authData.userAccess.needOferta) {
          await dispatch('dialog/openDialog', {
            blockName: 'registration',
            dialogName: 'ofertaPopup',
            payload: {
              loginMode: true,
              loginAction: async () => {
                await dispatch('loginSuccess', authData);
                await dispatch('profile/saveOfertaFlag', {}, { root: true });
              }
            }
          }, { root: true });
        }
        else {
          await dispatch('loginSuccess', authData);
        }
      }
      catch (error) {
        return await Promise.reject(error);
      }
    },

    async loginSuccess({rootState, commit, dispatch, state}: AuthStore, authData: IRequestAuth.IAuthDataResponse) {
      Vue.cookie.set(IAuth.CookieName.UserSometimeLogin, 'true', {expires: '10Y'});
      commit('setPassword', '');
      commit('setIsUser', true);
      commit('setProperty', {name: 'userAccess', value: authData.userAccess});
      commit('setProfileName', authData.profileName);

      let payload: OpenPageParams;
      const url = Vue.cookie.get(IAuth.CookieName.UrlForRedirectAfterLogin);
      const urlSavedAfterLogout = Vue.cookie.get(URL_FOR_REDIRECT_AFTER_LOGIN);

      if (url && url !== window.location.href) {
        payload = {routeName: ROUTE_NAME.REDIRECT, url};
        Vue.cookie.set(IAuth.CookieName.UrlForRedirectAfterLogin, '', {expires: '10Y'});
      }
      else if (urlSavedAfterLogout) {
        payload = {routeName: ROUTE_NAME.REDIRECT, url: urlSavedAfterLogout};
        Vue.cookie.set(URL_FOR_REDIRECT_AFTER_LOGIN, '', {expires: '10Y'});
      }
      else {
        payload = {routeName: ROUTE_NAME.HOME};
      }

      commit('setProperty', { name: 'tokenWasChecked', value: false });

      if (authData.userAccess.refFlag) {
        let url = `${window.location.origin}${IApp.RoutePath[IApp.ROUTE_NAME.PAGE_PROFILE]}?${ICorp.UrlGetParams.RefLink}=${state.login}`;
        payload = { routeName: IApp.ROUTE_NAME.REDIRECT, url };
      }

      const refCode = rootState.route.query[ICorp.UrlGetParams.RefLink];
      if (refCode && refCode !== '') {
        payload = { routeName: IApp.ROUTE_NAME.PAGE_PROFILE };
      }

      /**
       * ------------------------
       * Обновление счетчиков восстановления пароля и входа.
       * Если 2 раза зашли без использования восстановления пароля, то обунлять счетчики.
       */
      let useRecoverNow = JSON.parse(localStorage.getItem(IAuth.LocalStorageKeys.UseRecoverNow));
      let successLoginCount = +localStorage.getItem(IAuth.LocalStorageKeys.SuccessLoginCount);

      if (!useRecoverNow) {
        successLoginCount++;
        localStorage.setItem(IAuth.LocalStorageKeys.SuccessLoginCount, successLoginCount.toString());
      }

      if (successLoginCount >= 2) {
        successLoginCount = 0;
        localStorage.setItem(IAuth.LocalStorageKeys.RecoverPasswordCount, '0');
        localStorage.setItem(IAuth.LocalStorageKeys.SuccessLoginCount, successLoginCount.toString());
      }
      /** ------------------------ */

      return dispatch('openPage', payload, {root: true});
    },

    async logout({dispatch}: AuthStore, payload?: { withoutRedirect?: boolean; }) {
      let asyncAction = async () => {
        let params = { command: IRequestAuth.Command.Logout, };
        await httpResource.httpAction({ action: IRequestAuth.Action.Auth, params });
        await dispatch('_clearCookie');
        await dispatch('_setAnonymToken', Vue.cookie.get(AUTH_ANONYM_TOKEN));
        if (!payload || payload && !payload.withoutRedirect) await dispatch('openPage', {routeName: ROUTE_NAME.PAGE_AUTHORIZATION}, {root: true});
      };
      await dispatch('defaultResourceAction', { asyncAction }, { root: true });
    },

    async logoutConfirm({dispatch}: AuthStore) {
      await dispatch('confirm/open', {
        title: 'Подтверждение',
        text: 'Вы уверены, что хотите выйти?',
        cancel: { text: 'Нет', },
        confirm: { text: 'Да', rootAction: 'auth/logout', },
      }, { root: true, });
    },

    async sessionErrorResolve({commit, dispatch}: AuthStore) {
      let isShowDialogSession = false;
      if (Vue.cookie.get(AUTH_IS_USER_KEY) === 'true') {
        Vue.cookie.set(AUTH_IS_ACTUAL_ERROR_SESSION_MESSAGE, 'true', {expires: '10Y'});
        isShowDialogSession = true;
      }

      commit('setProperty', { name: 'isShowDialogSession', value: isShowDialogSession });
      Vue.cookie.set(AUTH_IS_SHOW_ERROR_SESSION_MESSAGE, isShowDialogSession.toString(), {expires: '10Y'});
      await dispatch('_clearCookie');
      await dispatch('openPage', {routeName: ROUTE_NAME.PAGE_AUTHORIZATION}, {root: true});
    },

    loadIsShowErrorSessionMessage({commit}: AuthStore) {
      if (Vue.cookie.get(AUTH_IS_ACTUAL_ERROR_SESSION_MESSAGE) === 'true') {
        commit('setProperty', { name: 'isShowDialogSession', value: Vue.cookie.get(AUTH_IS_SHOW_ERROR_SESSION_MESSAGE) === 'true' });
        Vue.cookie.set(AUTH_IS_ACTUAL_ERROR_SESSION_MESSAGE, 'false', {expires: '10Y'});
      }
    },

    async checkToken({state, commit, dispatch, rootState}: AuthStore) {
      try {
        await dispatch('saveUrlForExpiredSession');

        let params: any = { command: IRequestAuth.Command.CheckToken, };
        let response = await httpResource.httpAction({ action: IRequestAuth.Action.Auth, params });
        let authData: IRequestAuth.IAuthDataResponse = IRequestAuth.prepareAuthData(response.data.result);

        await dispatch('_setToken', authData.token);
        if (authData.needReload) {
          let clearPrams = { command: IRequestProfileSet.Commands.CLEAR_NEW_VERSION, };
          await httpResource.httpAction({ action: IRequestProfileSet.ACTION, params: clearPrams });
          return dispatch('openPage', {routeName: ROUTE_NAME.REDIRECT_WITHOUT_CACHE}, {root: true})
        }

        commit('setPassword', '');
        if (state.login !== '') {
          commit('setIsUser', true);
        }
        commit('setProperty', { name: 'userAccess', value: authData.userAccess });
        commit('setProfileName', authData.profileName);

        if (rootState.route.name === ROUTE_NAME.PAGE_AUTHORIZATION && state.isUser) {
          if (!Vue.cookie.get('isRedirectOnAuth')) {
            if (process.env.NODE_ENV == 'development') {
              Vue.cookie.set('isRedirectOnAuth', 'true', {expires: '15s'});
            }
            let payload = { routeName: ROUTE_NAME.HOME };
            return dispatch('openPage', payload, {root: true});
          }
        }
      }
      catch (error) {
        return dispatch('_clearCookie');
      }
    },

    async registrationNewUser({commit, dispatch, state}: AuthStore, payload: { registrationData: IAuth.DataRegistration }) {
      let asyncAction = async () => {
        const action = IRequestAuth.Action.RegistrationCreate
        let params: any = {};
        Object.keys(payload.registrationData).forEach(key => {
          if (Boolean(payload.registrationData[key])) {
            params[key] = payload.registrationData[key];
          }
        });
        await httpResource.httpAction({action, params});
      };
      return await dispatch('defaultResourceAction', { asyncAction }, { root: true });
    },

    registrationConfirm({commit, rootState, dispatch, state}: AuthStore) {
      const confirmRegistration = rootState.route.query[CONFIRM_REGISTRATION];
      const recoverPasswordGuid = rootState.route.query[RECOVER_PASSWORD];

      if (confirmRegistration) {
        return authResource.registrationConfirm(state.token, confirmRegistration)
          .then((response: AxiosResponse<IResponseData<IAuth.ResourceAuthRegistrationConfirmResponseDataResult>>) => {
            return dispatch('_clearCookie')
              .then(() => {
                commit('setLogin', response.data.result.login);
                commit('setPassword', response.data.result.password);
                router.replace({query: {}});
              });
          })
          .catch((error) => {
            return dispatch('_clearCookie')
              .then(() => {
                router.replace({query: {}});
                const params: any = {textId: 'error.createdUserWasConfirmed'};
                return dispatch('error/open', params, {root: true});
              });
          });
      }
      if (recoverPasswordGuid) {
        return authResource.registrationConfirm(state.token, recoverPasswordGuid)
          .then((response: AxiosResponse<IResponseData<IAuth.ResourceAuthRegistrationConfirmResponseDataResult>>) => {
            return dispatch('_clearCookie')
              .then(() => {
                commit('setLogin', response.data.result.login);
                commit('setPassword', response.data.result.password);
                commit('setRecoverPassword', response.data.result.password);
                router.replace({query: {}});
              });
          })
          .catch((error) => {
            router.replace({query: {}});
            let params: any = {textId: 'error.timeIsUpToConfirmPassword'};
            if (error instanceof AppError && error.status === IAuth.RECOVER_PASSWORD_EMAIL_NOT_FOUND) {
              params = {textId: 'error.passwordWasConfirmedLater'};
            }
            return dispatch('error/open', params, {root: true});
          });
      }
    },

    async recoverPassword({commit, dispatch, state}: AuthStore, login: string) {
      let asyncAction = async () => {
        let params: any = {
          command: IRequestAuth.Command.RecoverPassword,
          login: login,
        };
        await httpResource.httpAction({action: IRequestProfileSet.ACTION, params});
        await dispatch('openSuccessesNotice', 'Отправили Вам новый пароль');
      };
      return await dispatch('defaultResourceAction', { asyncAction }, { root: true });
    },

    saveUrlForExpiredSession() {
      // if session break, save url for redirect after
      if (Vue.cookie.get(AUTH_IS_SHOW_ERROR_SESSION_MESSAGE) === 'false') {
        Vue.cookie.set(IAuth.CookieName.UrlForRedirectAfterLogin, window.location.href, {expires: '10Y'});
      }
    },

    saveUrlForRedirectAfterLogin() {
      // if user logout and go to link, save url for redirect after login
      if (!Vue.cookie.get(URL_FOR_REDIRECT_AFTER_LOGIN) || Vue.cookie.get(URL_FOR_REDIRECT_AFTER_LOGIN) === '') {
        Vue.cookie.set(URL_FOR_REDIRECT_AFTER_LOGIN, window.location.href, {expires: '10Y'});
      }
    },

    async openSuccessesNotice({ dispatch }: AuthStore, text: string) {
      bus.$notify({
        title: 'Успешно',
        message: text,
        type: 'success'
      });
    },

    _auth({ commit, dispatch, state, rootState }: AuthStore) {
      commit('setNewSession');
      const anonymousPages = [
        ROUTE_NAME.PAGE_AUTHORIZATION,
        ROUTE_NAME.PAGE_REGISTRATION,
        ROUTE_NAME.PAGE_SUPPORT,
        ROUTE_NAME.PAGE_SCREEN_PAY,
      ];
      if (!state.token && !anonymousPages.includes(rootState.route.name)) {
        return dispatch('openPage', {routeName: ROUTE_NAME.PAGE_AUTHORIZATION}, {root: true});
      }
    },

    _setToken({commit}: AuthStore, token: string) {
      Vue.cookie.set(AUTH_TOKEN_KEY, token, {expires: '10Y'});
      commit('setToken', token);
    },

    _setAnonymToken({commit, dispatch}: AuthStore, token: string) {
      Vue.cookie.set(AUTH_ANONYM_TOKEN, token, {expires: '10Y'});
      commit('setToken', token);
      return dispatch('_setToken', token);
    },

    _loadUser({commit}: AuthStore) {
      try {
        const isUser = JSON.parse(Vue.cookie.get(AUTH_IS_USER_KEY)) === true;
        commit('setIsUser', isUser);
        if (isUser) {
          commit('setLogin', Vue.cookie.get(AUTH_LOGIN_KEY));
        }
      } catch (e) {
        commit('setIsUser', false);
      }
    },

    _clearCookie({commit}: AuthStore) {
      commit('setIsUser', false);
      commit('setLogin', '');
      commit('setProfileName', '');
    },
  },
};
